import { mapMaybe } from '@execonline-inc/collections';
import { always, identity } from '@kofno/piper';
import { fromEmpty, just, fromNullable } from 'maybeasy';
import { err, ok, Result } from 'resulty';
import {
  BooleanField,
  DateField,
  FileField,
  FileValue,
  fileWithinSizeLimit,
  FormField,
  isRequired,
  KeepFile,
  MultipleSelectionsField,
  NewFile,
  NumericField,
  SingleSelectionField,
  StringField,
} from '../Types';

export type ValidatedField<F extends FormField> = Result<F, F>;

export const asError = <T extends FormField>(field: T, msg: string): ValidatedField<T> =>
  err({ ...field, errors: [msg] });

export const presentWhenRequired = <F extends FormField>(field: F): ValidatedField<F> => {
  if (!isRequired(field)) {
    return ok(field);
  }

  switch (field.kind) {
    case 'string':
    case 'multiple-selection':
    case 'file':
      return fromEmpty(field.value)
        .map<ValidatedField<F>>(always(ok(field)))
        .getOrElse(() => asError(field, 'is required'));
    case 'single-selection':
      return fromNullable(field.value)
        .map<ValidatedField<F>>(always(ok(field)))
        .getOrElse(() => asError(field, 'is required'));
    case 'boolean':
      return ok(field);
    case 'date':
    case 'number':
      return field.value
        .map<ValidatedField<F>>(() => ok(field))
        .getOrElse(() => asError(field, 'is required'));
  }
};

export const notTooLong = (field: StringField): ValidatedField<StringField> =>
  field.value.length > field.maxlength
    ? asError(field, `is too long, max length is: ${field.maxlength}`)
    : ok(field);

export const notTooShort = (field: StringField): ValidatedField<StringField> =>
  field.minlength
    .map<
      ValidatedField<StringField>
    >((min) => (field.value.length < min ? asError(field, `is too short, min length is: ${min}`) : ok(field)))
    .getOrElse(() => ok(field));

export const matchesPatternIfPresent = (field: StringField): ValidatedField<StringField> =>
  field.pattern
    .map<
      ValidatedField<StringField>
    >((pattern) => (pattern.test(field.value) ? ok(field) : asError(field, `does not match pattern: ${pattern}`)))
    .getOrElse(() => ok(field));

export const validateStringField = (field: StringField): ValidatedField<StringField> =>
  presentWhenRequired(field)
    .andThen(notTooLong)
    .andThen(notTooShort)
    .andThen(matchesPatternIfPresent);

export const validateBooleanField = (field: BooleanField): ValidatedField<BooleanField> =>
  presentWhenRequired(field);

export const notTooEarly = (field: DateField): ValidatedField<DateField> =>
  just({})
    .assign('value', field.value)
    .assign('min', field.min)
    .map<ValidatedField<DateField>>(({ value, min }) =>
      value.valueOf() < min.valueOf()
        ? asError(field, `is too early, min date is: ${min}`)
        : ok(field),
    )
    .getOrElse(() => ok(field));

export const notTooLate = (field: DateField): ValidatedField<DateField> =>
  just({})
    .assign('value', field.value)
    .assign('max', field.max)
    .map<ValidatedField<DateField>>(({ value, max }) =>
      value.valueOf() > max.valueOf()
        ? asError(field, `is too late, max date is: ${max}`)
        : ok(field),
    )
    .getOrElse(() => ok(field));

export const validateDateField = (field: DateField): ValidatedField<DateField> =>
  presentWhenRequired(field).andThen(notTooEarly).andThen(notTooLate);

export const notTooSmall = (field: NumericField): ValidatedField<NumericField> => {
  return just({})
    .assign('value', field.value)
    .assign('min', field.min)
    .map<ValidatedField<NumericField>>(({ value, min }) =>
      value.valueOf() < min.valueOf()
        ? asError(field, `is too small, min value is: ${min}`)
        : ok(field),
    )
    .getOrElse(() => ok(field));
};

export const notTooBig = (field: NumericField): ValidatedField<NumericField> =>
  just({})
    .assign('value', field.value)
    .assign('max', field.max)
    .map<ValidatedField<NumericField>>(({ value, max }) =>
      value.valueOf() > max.valueOf()
        ? asError(field, `is too big, max value is: ${max}`)
        : ok(field),
    )
    .getOrElse(() => ok(field));

export const validateNumericField = (field: NumericField): ValidatedField<NumericField> =>
  presentWhenRequired(field).andThen(notTooSmall).andThen(notTooBig);

export const notTooFew = (
  field: MultipleSelectionsField,
): ValidatedField<MultipleSelectionsField> =>
  field.value.length < field.minSelections
    ? asError(field, `has too few selections, min is: ${field.minSelections}`)
    : ok(field);

export const notTooMany = (
  field: MultipleSelectionsField,
): ValidatedField<MultipleSelectionsField> =>
  field.value.length > field.maxSelections
    ? asError(field, `has too many selections, max is: ${field.maxSelections}`)
    : ok(field);

export const exactlyOne = (field: SingleSelectionField): ValidatedField<SingleSelectionField> => {
  const singleSelectionToArray = fromNullable(field.value)
    .map((val) => [val])
    .getOrElse(() => []);

  return singleSelectionToArray.length === 1
    ? ok(field)
    : asError(field, 'has too many selections, max is: 1');
};

export const validateMultipleSelectionField = (
  field: MultipleSelectionsField,
): ValidatedField<MultipleSelectionsField> =>
  presentWhenRequired(field).andThen(notTooFew).andThen(notTooMany);

export const validateSingleSelectionField = (
  field: SingleSelectionField,
): ValidatedField<SingleSelectionField> => presentWhenRequired(field).andThen(exactlyOne);

export const isNewOrKeepFile = (file: FileValue): file is NewFile | KeepFile => {
  switch (file.kind) {
    case 'new-file':
    case 'keep-file':
      return true;
    case 'delete-file':
      return false;
  }
};

export const singleFileIfMultipleIsDisallowed = (field: FileField): ValidatedField<FileField> =>
  field.value.filter(isNewOrKeepFile).length > 1 && !field.multiple
    ? asError(field, 'multiple files are not accepted')
    : ok(field);

export const isNewFile = (file: FileValue): file is NewFile => {
  switch (file.kind) {
    case 'new-file':
      return true;
    case 'keep-file':
    case 'delete-file':
      return false;
  }
};

export const newFilesAreWithinSizeLimit = (field: FileField): ValidatedField<FileField> =>
  mapMaybe((newFile) => newFile.file, field.value.filter(isNewFile)).reduce<
    ValidatedField<FileField>
  >(
    (validatedField, file) =>
      validatedField.andThen((f) =>
        fileWithinSizeLimit(f, file)
          .map(always(f))
          .orElse((e) => asError(f, e)),
      ),
    ok(field),
  );

export const validateFileField = (field: FileField): ValidatedField<FileField> =>
  presentWhenRequired(field)
    .andThen(singleFileIfMultipleIsDisallowed)
    .andThen(newFilesAreWithinSizeLimit);

export const validateField = (field: FormField): ValidatedField<FormField> => {
  switch (field.kind) {
    case 'string':
      return validateStringField(field);
    case 'boolean':
      return validateBooleanField(field);
    case 'date':
      return validateDateField(field);
    case 'number':
      return validateNumericField(field);
    case 'multiple-selection':
      return validateMultipleSelectionField(field);
    case 'single-selection':
      return validateSingleSelectionField(field);
    case 'file':
      return validateFileField(field);
  }
};

// If all fields are valid, return the collection in an ok. If any fields are invalid, return the
// collection in an err with appropriate errors for the invalid fields on those fields.
export const validateFields = (
  fields: ReadonlyArray<FormField>,
): Result<ReadonlyArray<FormField>, ReadonlyArray<FormField>> => {
  return fields
    .map(validateField)
    .reduce(
      (resultArray, validatedField) =>
        resultArray
          .andThen((a) => validatedField.map((f) => [...a, f]).mapError(() => a))
          .mapError((a) => [...a, validatedField.cata({ Err: identity, Ok: identity })]),
      ok<ReadonlyArray<FormField>, ReadonlyArray<FormField>>([]),
    );
};
